import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaFilePdf, FaPrint, FaSearch, FaFilter, FaTrash } from 'react-icons/fa';
import { getResults, exportResultsExcel, deleteStudentResult } from '../../api/resultsApi';
import { Link, useParams } from 'react-router-dom';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const ResultsPage = () => {
  const { driveId } = useParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });
  const [deletingId, setDeletingId] = useState(null);

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResults();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterBranch, filterStatus, sortOrder]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const res = await getResults({ driveId, branch: filterBranch, status: filterStatus, sort: sortOrder, search: searchTerm });
      setResults(res.data);
    } catch (err) {
      console.error('Failed to fetch results', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (r) => {
    const attemptId = r.attempt?.id;
    if (!attemptId) return;
    const name = r.studentName || r.attempt?.student?.email || 'this student';
    const ok = window.confirm(
      `Delete ${name}?\n\nThis permanently removes their registration, answers, result, violations and uploaded files for this assessment. This cannot be undone.`
    );
    if (!ok) return;

    setDeletingId(attemptId);
    try {
      await deleteStudentResult(attemptId);
      setResults(prev => prev.filter(x => x.attempt?.id !== attemptId));
      showCustomAlert('Student data deleted successfully.');
    } catch (err) {
      console.error('Failed to delete student result', err);
      showCustomAlert(err.response?.data?.message || 'Failed to delete student data.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await exportResultsExcel({ driveId, branch: filterBranch, status: filterStatus, search: searchTerm, sort: sortOrder });
      
      console.log('Response Headers:', response.headers);
      console.log('Content-Type:', response.headers['content-type']);
      console.log('Content-Length:', response.headers['content-length']);
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'results.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export Error:', err);
      showCustomAlert('Failed to export Excel');
    }
  };


  return (
    <div className="container-fluid p-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold mb-1">Results & Analytics</h3>
          <p className="text-muted mb-0">View, filter, and export student performance data.</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-success d-flex align-items-center gap-2 rounded-pill shadow-sm" onClick={handleExportExcel}>
            <FaFileExcel /> Export Excel
          </button>
          <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill shadow-sm" onClick={() => window.print()}>
            <FaPrint /> Print
          </button>
        </div>
      </div>

      <div className="card custom-card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaSearch className="text-muted" /></span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search by name, email or ID..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaFilter className="text-muted" /></span>
                <select className="form-select" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
                  <option value="">All Branches</option>
                  <option value="CSE">Computer Science</option>
                  <option value="IT">Information Tech</option>
                  <option value="ECE">Electronics</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Rejected">Rejected</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="">Sort By Score</option>
                <option value="desc">Highest First</option>
                <option value="asc">Lowest First</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-muted border-bottom-0">
                <tr>
                  <th className="py-3 px-4 fw-semibold border-0 rounded-start">Photo</th>
                  <th className="py-3 px-4 fw-semibold border-0">Student Name</th>
                  <th className="py-3 px-4 fw-semibold border-0">Score</th>
                  <th className="py-3 px-4 fw-semibold border-0">Easy Attempted</th>
                  <th className="py-3 px-4 fw-semibold border-0">Medium Attempted</th>
                  <th className="py-3 px-4 fw-semibold border-0">Hard Attempted</th>
                  <th className="py-3 px-4 fw-semibold border-0">Percentage</th>
                  <th className="py-3 px-4 fw-semibold border-0">Pass/Fail</th>
                  <th className="py-3 px-4 fw-semibold border-0">Selection Status</th>
                  <th className="py-3 px-4 fw-semibold border-0 text-end">Violations</th>
                  <th className="py-3 px-4 fw-semibold border-0 text-center rounded-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="11" className="text-center py-4"><div className="spinner-border text-primary-custom"></div></td></tr>
                ) : results.length === 0 ? (
                  <tr><td colSpan="11" className="text-center py-4 text-muted">No results found from backend.</td></tr>
                ) : (
                  results.map(r => (
                    <tr key={r.id} className={`border-bottom ${r.attempt?.status === 'Violated' ? 'bg-danger bg-opacity-10' : ''}`}>
                      <td className="py-3 px-4">
                        {r.faceImageUrl ? (
                          <img src={`http://localhost:5000/uploads/${r.faceImageUrl}`} alt="Student Face" className="rounded-circle" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                        ) : (
                          <div className="rounded-circle bg-light d-flex align-items-center justify-content-center text-muted border" style={{ width: '60px', height: '60px', fontSize: '0.75rem' }}>No Photo</div>
                        )}
                      </td>
                      <td className="py-3 px-4 fw-bold">
                        <Link to={`/admin/drives/${driveId || r.attempt?.assessment?.driveId}/results/${r.attempt?.id}`} className="text-decoration-none text-dark hover-primary">
                          {r.studentName || r.attempt?.student?.email || r.attempt?.student?.id}
                        </Link>
                      </td>
                      <td className="py-3 px-4">
                        {r.attempt?.status === 'Violated' ? (
                          <span className="fw-bold text-danger">Auto-Submitted</span>
                        ) : (
                          <><span className={`fw-bold ${r.obtainedMarks >= (r.attempt?.assessment?.totalMarks * 0.75 || 0) ? 'text-success' : r.obtainedMarks >= (r.attempt?.assessment?.totalMarks * 0.5 || 0) ? 'text-warning' : 'text-danger'}`}>{r.obtainedMarks}</span> / {r.attempt?.assessment?.totalMarks}</>
                        )}
                      </td>
                      <td className="py-3 px-4 fw-medium">{r.easyAttempted || 0}</td>
                      <td className="py-3 px-4 fw-medium">{r.mediumAttempted || 0}</td>
                      <td className="py-3 px-4 fw-medium">{r.hardAttempted || 0}</td>
                      <td className="py-3 px-4">{r.percentage?.toFixed(2)}%</td>
                      <td className="py-3 px-4">
                        <span className={`badge rounded-pill px-3 py-2 ${
                          r.passOrFail === 'Pass' ? 'bg-success' :
                          r.passOrFail === 'Fail' ? 'bg-danger' :
                          'bg-secondary'
                        }`}>{r.passOrFail || 'Pending'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`badge rounded-pill px-3 py-2 ${
                          r.selectionStatus === 'Shortlisted' ? 'bg-success' :
                          r.selectionStatus === 'Rejected' ? 'bg-danger' :
                          'bg-warning text-dark'
                        }`}>{r.selectionStatus || 'Pending'}</span>
                      </td>
                      <td className="py-3 px-4 text-end fw-bold">
                        {r.violationCount || 0}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete this student's data"
                          disabled={deletingId === r.attempt?.id}
                          onClick={() => handleDelete(r)}
                        >
                          {deletingId === r.attempt?.id ? <span className="spinner-border spinner-border-sm"></span> : <FaTrash />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default ResultsPage;
